/* Display-only carousel. No catalog, pricing, cart or checkout mutations. */
(() => {
  const init = (root) => {
    if (root.dataset.ccGiftReady === 'true') return;
    root.dataset.ccGiftReady = 'true';
    const slides = [...root.querySelectorAll('[data-cc-gift-slide]')];
    const tabs = [...root.querySelectorAll('[data-cc-gift-index]')];
    const motionButton = root.querySelector('[data-cc-gift-motion]');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const liveRegion = root.querySelector('.cc-gift-hero__slides');
    let index = 0, timer = null, focusTimer = null, disposed = false, hovered = root.matches(':hover'), focusPaused = root.contains(document.activeElement), explicitMotion = false, paused = root.dataset.autoplay !== 'true' || reduced.matches;
    const interval = Math.min(16000, Math.max(3000, Number(root.dataset.interval) || 3000));
    // Measure the real announcement/header/category stack instead of assuming 350px.
    // Copy stays in normal flow: very short windows scroll instead of clipping text.
    const frame = root.querySelector('.cc-gift-hero__frame');
    let fitTimer = null;
    const fit = () => {
      fitTimer = null;
      if (disposed || !root.isConnected) return;
      if (window.innerWidth <= 749) {
        root.style.removeProperty('--cc-gift-stage-height');
        delete root.dataset.ccGiftFit;
        return;
      }
      const rootStyle = getComputedStyle(root);
      const padding = (style) => parseFloat(style.paddingTop) + parseFloat(style.paddingBottom);
      let flowTop = root.getBoundingClientRect().top + window.scrollY;
      // Horizon scrolls .page-wrapper, not the document. Undo ancestor scroll too.
      for (let parent = root.parentElement; parent && parent !== document.scrollingElement; parent = parent.parentElement) flowTop += parent.scrollTop;
      const top = Math.max(0, flowTop);
      const available = window.innerHeight - top - padding(rootStyle) - 16;
      const height = Math.round(Math.max(240, Math.min(frame.clientWidth / 2, available)));
      const value = height + 'px';
      if (root.style.getPropertyValue('--cc-gift-stage-height') !== value) root.style.setProperty('--cc-gift-stage-height', value);
      const mode = height < 500 ? 'compact' : 'regular';
      if (root.dataset.ccGiftFit !== mode) root.dataset.ccGiftFit = mode;
    };
    const queueFit = () => { if (!disposed && fitTimer === null) fitTimer = window.requestAnimationFrame(fit); };
    const fitObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(queueFit) : null;
    const observeLayout = () => {
      fitObserver?.disconnect();
      fitObserver?.observe(frame);
      document.querySelectorAll('.shopify-section-group-header-group, header-component').forEach(element => fitObserver?.observe(element));
    };
    const layoutChanged = () => { if (!disposed) { observeLayout(); queueFit(); } };
    const layoutEvents = ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder'];
    observeLayout();
    layoutEvents.forEach(type => document.addEventListener(type, layoutChanged));
    window.addEventListener('resize', queueFit, {passive:true});
    window.visualViewport?.addEventListener('resize', queueFit, {passive:true});
    document.fonts?.addEventListener('loadingdone', queueFit);
    document.fonts?.ready.then(queueFit);
    const stop = () => { if (timer !== null) window.clearTimeout(timer); timer = null; };
    const motionState = () => {
      // Reduced motion starts in manual mode; the visitor may explicitly play.
      motionButton.disabled = slides.length < 2;
      motionButton.setAttribute('aria-label', paused ? root.dataset.playLabel : root.dataset.pauseLabel);
      motionButton.setAttribute('aria-pressed', String(!paused));
      root.querySelector('[data-cc-gift-pause-icon]').hidden = paused;
      root.querySelector('[data-cc-gift-play-icon]').hidden = !paused;
    };
    const show = (next) => {
      index = (next + slides.length) % slides.length;
      slides.forEach((slide, i) => { slide.hidden = i !== index; });
      tabs.forEach((tab, i) => tab.setAttribute('aria-current', String(i === index)));
    };
    const schedule = () => {
      stop();
      if (!disposed && root.isConnected && !hovered && !focusPaused && !paused && (!reduced.matches || explicitMotion) && !document.hidden) {
        timer = window.setTimeout(() => { show(index + 1); schedule(); }, interval);
      }
    };
    const manual = (next) => { paused = true; stop(); liveRegion.setAttribute('aria-live','polite'); show(next); motionState(); };
    root.querySelector('[data-cc-gift-prev]').addEventListener('click', () => manual(index - 1));
    root.querySelector('[data-cc-gift-next]').addEventListener('click', () => manual(index + 1));
    tabs.forEach((tab) => tab.addEventListener('click', () => manual(Number(tab.dataset.ccGiftIndex))));
    motionButton.addEventListener('click', () => {
      paused = !paused;
      explicitMotion = !paused;
      liveRegion.setAttribute('aria-live', paused ? 'polite' : 'off');
      motionState();
      // Explicit play resumes keyboard-focus pause, but never hover pause.
      // Moving focus to another control pauses again.
      if (!paused) focusPaused = false;
      schedule();
    });
    root.addEventListener('keydown', (event) => {
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement || event.target instanceof HTMLSelectElement) return;
      event.preventDefault();
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : index + (event.key === 'ArrowRight' ? 1 : -1);
      manual(next);
      tabs[index].focus();
    });
    root.addEventListener('mouseenter', () => { hovered = true; stop(); });
    root.addEventListener('mouseleave', () => { hovered = false; schedule(); });
    root.addEventListener('focusin', () => { focusPaused = true; stop(); });
    const focusLeft = () => {
      if (focusTimer !== null) window.clearTimeout(focusTimer);
      focusTimer = window.setTimeout(() => {
        focusTimer = null;
        if (disposed) return;
        if (!root.contains(document.activeElement)) focusPaused = false;
        schedule();
      }, 0);
    };
    root.addEventListener('focusout', focusLeft);
    document.addEventListener('visibilitychange', schedule);
    const preferenceChanged = () => { if (reduced.matches) { explicitMotion = false; paused = true; } motionState(); schedule(); };
    reduced.addEventListener('change', preferenceChanged);
    const unloaded = (event) => {
      if (!event.target.contains(root)) return;
      disposed = true;
      stop();
      if (fitTimer !== null) window.cancelAnimationFrame(fitTimer);
      fitTimer = null;
      fitObserver?.disconnect();
      window.removeEventListener('resize', queueFit);
      window.visualViewport?.removeEventListener('resize', queueFit);
      document.fonts?.removeEventListener('loadingdone', queueFit);
      layoutEvents.forEach(type => document.removeEventListener(type, layoutChanged));
      if (focusTimer !== null) window.clearTimeout(focusTimer);
      focusTimer = null;
      root.removeEventListener('focusout', focusLeft);
      document.removeEventListener('visibilitychange', schedule);
      reduced.removeEventListener('change', preferenceChanged);
      document.removeEventListener('shopify:section:unload', unloaded);
      delete root.dataset.ccGiftReady;
    };
    document.addEventListener('shopify:section:unload', unloaded);
    show(0); fit(); motionState(); schedule();
  };
  document.querySelectorAll('[data-cc-gift-carousel]').forEach(init);
  document.addEventListener('shopify:section:load', (event) => event.target.querySelectorAll('[data-cc-gift-carousel]').forEach(init));
})();
